import styles from "@/styles/search.module.scss"
import Head from "next/head"
import DefaultLayout from "@/components/DefaultLayout"
import SliderBarLeft from "@/components/SliderBarLeft"
import SliderBarRight from "@/components/SliderBarRight"
import ShopInfo from "@/components/ShopInfo"
import BreadCrum from '@/components/global/BreadCrum'
import GoodsComponentWithDiscountNew from '@/components/GoodsComponentWithDiscountNew'
import CategoryFilter from "@/components/search/CategoryFilter"
import FilterItem from "@/components/search/FilterItem"
import NewArrivals from "@/components/category/NewArrivals"
import LatestReviews from "@/components/category/LatestReviews"
import GoogleADSense from "@/components/GoogleADSense"

import {
  CaretLeftOutlined,
  CaretRightOutlined,
  CloseOutlined
} from '@ant-design/icons';
import type { PaginationProps } from 'antd';
import { Select, Pagination, Spin } from 'antd';

import dayjs from 'dayjs'
import { debounce } from 'lodash';
import React, { useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'
import Link from "next/link"
import { setCookie } from '@/utils/cookie'
import categoryApi from '@/service/modules/categoryApi'
import searchApi from '@/service/modules/searchApi'

interface ProductItem {
  productID: string;
  name: string;
  url: string;
  imgUrl: string;
  reviewCount: number;
  price: number;
  normalPrice: number;
  freeShipping: number;
  onSale: number;
  reduced: number;
  isNew: number;
  position?: number,
  location?: string;
  isEmailOnly: any;
  inStock?: number;
  isGroup?: number;
  promotionTypeID: string | number;
  promotion: {
    productID: number;
    promotionType: number;
    promotionPrice: number;
    normalPrice: number;
    endDate: string;
    status: number;
    notified: number;
    pointRate: string;
    promotionRate: number;
  };
  review: {
    avgRank: number;
    total: number;
  };
  shortDescription?: string;
  visitor?: {
    weeklyVisitors: number;
  }
  rankNum?: number;
}

interface BreadcrumbItem {
  name: string;
  url: string;
  current: boolean;
}

// 定义分类项接口
interface sideFilterItem {
  name: string;
  count?: number;
  minPrice?: number | null;
  maxPrice?: number | null;
}

interface filterTypeItem {
  title: string;
  key: string;
  currentItemName?: string;
  items: sideFilterItem[]
}

// 分类筛选接口
interface cateFilterItem {
  name: string;
  categoryID: number | string;
  url: string;
  subCategories?: cateFilterItem[];
}

interface cateFilterType {
  title: string;
  key: string;
  currentCategoryId?: string;
  items: cateFilterItem[]
}

interface hotSearchKeywordItem {
  keywords: string;
  url: string;
}

interface areaItem {
  name: string;
  url: string;
}

interface relatedKeywordItem {
  name: string;
  url: string;
}

interface Param {
  categoryID: number | string;
  currentCategoryName: string;
  currentCategoryID: number | string;
}

// 获取分类列表数据
async function getSearchData(params: any) {
  try {
    const dataObj = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null && value !== '')
    );
    let productData = await searchApi.getShopResult(dataObj)

    if (
      productData.data 
      && productData.data.pageList 
      && productData.data.pageList.items
      && productData.data.pageList.items.length
    ) {
      productData.data.pageList.items.forEach((item: any) => {
        item.imgUrl = item.filename || item.newThumbnail
      })
    }

    return {
      ...productData.data
    }
  } catch (error) {
    console.log('Next.js Error :', dayjs().format('YY-MM-DD HH:mm:ss'), `  Page Search: /search/${params.keyword}/ Data acquisition failed:`, JSON.stringify(error))
  }
}

export default function PageShop() {
  const { Option } = Select
  const router = useRouter()

  const [searchKeyword, setSearchKeyword] = useState('')
  const [searchCategoryFromUrl, setSearchCategoryFromUrl] = useState('')

  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [updating, setUpdating] = useState(false);
  const [filterStr, setFilterStr] = useState('');
  const [filterDivs, setFilterDivs] = useState<React.ReactElement[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(48);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [pageProductData, setPageProductData] = useState<ProductItem[]>([]);
  const [pageParams, setPageParams] = useState<Param>({
    categoryID: 0,
    currentCategoryName: '',
    currentCategoryID: 0
  });

  const [breadCrumb, setBreadCrumb] = useState<BreadcrumbItem[]>([]);
  const [categoryId, setCategoryId] = useState<number | string>('');

  const [sidebarFilter, setSidebarFilter] = useState<filterTypeItem[]>([]);
  const [sidebarCateFilter, setSidebarCateFilter] = useState<cateFilterType[]>([]);
  const [sidebarFilterQuery, setSidebarFilterQuery] = useState<any[]>([]);
  const [relatedKeywords, setRelatedKeywords] = useState<relatedKeywordItem[]>([]);
  const [hotSearchKeyword, setHotSearchKeyword] = useState<hotSearchKeywordItem[]>([]);

  const [popularKeywords, setPopularKeywords] = useState('');
  const [shopArea, setShopArea] = useState<areaItem[]>([]);
  const [categoryName, setCategoryName] = useState('');
  const [aboutCategory, setAboutCategory] = useState('');

  interface suburbOptionsItem {
    id: string;
    name: string
  }

  interface sortByFilterType{
    sortBy: string;
    sortDirection: string;
  }
  const [suburbOptions, setSuburbOptions] = useState<suburbOptionsItem[]>([]);
  const [sortBy, setSortBy] = useState<string | null | undefined>(null);
  const [isFreeShipping, setIsFreeShipping] = useState(false);
  const [sortByFilter, setSortByFilter] = useState<sortByFilterType>({ sortBy: '', sortDirection: '' });
  const [pageSize, setPageSize] = useState(48)

  // pageSize列表, 用于选择
  let pageSizeList = [
    { 
      id: 1,
      name: '48',
      value: 48
    },
    { 
      id: 2,
      name: '100',
      value: 100
    },
    { 
      id: 3,
      name: '200',
      value: 200
    }
  ]

  // 获取页面extra数据
  async function getCategoryExtraData(id: string | number) {
    try {
      let resData = await categoryApi.getCategoryExtra(id)

      if (resData.data && resData.data) {
        setPopularKeywords(resData.data.popularKeywords)
        setHotSearchKeyword(resData.data.hotSearchKeyword)
        setCategoryName(resData.data?.category.name || '')
        setAboutCategory(resData.data?.category.description || '')

        // area 数据处理
        if (resData.data.area && resData.data.area.length) {
          let areaList = resData.data.area.map((item: string) => {
            // 调整为首字母大写
            return {
              name: item.charAt(0).toUpperCase()+ item.slice(1),
              url: `/shop/${searchKeyword.toString().replace(/ /g, '+')}+${item}`
            }
          })
          setShopArea(areaList)
        }
      }
    } catch (error) {
      console.log(`shop页面extra数据获取失败： `, error)
    }
  }

  // 获取页面数据(主要是初始化请求，数据筛选的请求在 handleUpdateData)
  const getPageData = async(keyword?: string | number, categoryFromUrl?: string) => {
    try {
      // 开启页面loading
      if (keyword) {
        setUpdating(true)
      } else {
        return
      }

      const data = await getSearchData({ 
        keyword: keyword || searchKeyword,
        category: categoryFromUrl || searchCategoryFromUrl,
        page: 1,
        perPage: 48,
        isFreeShpping: 0,
        sortBy: 'bestMatch',
        sortDirection: 'desc' 
      })

      // 首次请求，给keyword和CategoryFromUrl赋值，后面做筛选使用
      if (keyword) {
        setSearchKeyword(String(keyword || searchKeyword))
        setSearchCategoryFromUrl(categoryFromUrl || searchCategoryFromUrl || '')
      }

      setUpdating(false)
  
      if (data) {
        // 优先判断，是否有replaceUrl
        if (data?.link?.replaceUrl) {
          // 获取URL上的参数用以跳转
          var url = router.asPath || ''
          const urlParams = url.split('?')[1] ? `?${url.split('?')[1]}` : ''
  
          window.location.replace(`${data.link.replaceUrl + urlParams}`)
        } else {
          let categoryOriginalData = data.sidebarFilter && data.sidebarFilter.length ? data.sidebarFilter.filter((item: any) => item.key == 'category') : []
          let categoryFilterData = JSON.parse(JSON.stringify(categoryOriginalData))
          
          if (data.param) {
            categoryFilterData[0].currentCategoryId = data.param.currentCategoryID
          }
    
          let filtersData = data.sidebarFilter.filter((item: any) => item.key != 'category' && item.items.length)
  
          setCategoryId(data.categoryID)
  
          setPageParams(data.param || {
            categoryID: 0,
            currentCategoryName: '',
            currentCategoryID: 0
          })
  
          setBreadCrumb(data.breadCrumb || [])

          if (page != 1) setPage(1)
          setLastPage(data.pageList.lastPage)
          setPerPage(data.pageList.perPage)
          setTotal(data.pageList.total)
          setPageProductData(data.pageList.items)
  
          setSidebarFilter(filtersData || [])
          setSidebarCateFilter(categoryFilterData || [])
  
          setHotSearchKeyword(data.hotSearchKeyword || [])
          setRelatedKeywords(data.relatedKeywords || [])
  
          // 获取分类页面extra数据
          getCategoryExtraData(data.categoryID)

          // 延迟关闭页面loading
          setTimeout(() => {
            setIsFirstLoad(false)
          }, 400);
        }
      } else {
        setIsFirstLoad(false)
        setUpdating(false)
      }
    } catch (error) {
      setIsFirstLoad(false)
      setUpdating(false)
    }
  }

  /**
   * 初始化请求页面数据
   * 1、判断url中是否有aid参数
   * 2、如果没有aid就直接请求
   * 3、包含aid参数，就延迟一些请求(等待setCookie)
   */
  useEffect(() => {
    if (router.isReady) {
      // 获取请求参数 keyword 和 categoryFromUrl
      let keyword = ''
      let categoryFromUrl = ''
      const pathSegments = router.asPath.split('?')[0].split('/'); // 去掉查询参数并分割路径段
      if (pathSegments.length >= 5) { // 确保路径段的长度足够
        keyword = pathSegments[2]; // 获取 keyword
        categoryFromUrl = pathSegments[4]; // 获取 categoryFromUrl
      } else {
        keyword = pathSegments[2] || '';
      }

      const { aid, recoveraid }: any = router.query
      if (aid || recoveraid) {
        setTimeout(() => {
          getPageData(keyword, categoryFromUrl)
        }, 100)
      } else {
        getPageData(keyword, categoryFromUrl)
      }
    }
  }, [router.isReady]);

  // 判断是否有aid 或 recoveraid在url，有就将其
  useEffect(() => {
    const { aid, recoveraid }: any = router.query
    let lastAidValue = ''
    let lastRecoveraidValue = ''

    // 检查是否有"aid"参数，如有多个取最后
    if (Array.isArray(aid) && aid.length > 0) {
      lastAidValue = aid[aid.length - 1]
    } else if (typeof aid === 'string') {
      // 如果只有一个"aid"参数，它将是一个字符串
      lastAidValue = aid
    }

    // 检查是否有"recoveraid"参数，如有多个取最后
    if (Array.isArray(recoveraid) && recoveraid.length > 0) {
      lastRecoveraidValue = recoveraid[recoveraid.length - 1]
    } else if (typeof recoveraid === 'string') {
      // 如果只有一个"recoveraid"参数，它将是一个字符串
      lastRecoveraidValue = recoveraid
    }

    // 优先于异步请求，将aid存入cookie, 60秒过期（后续会发起请求重新设置）
    if (lastAidValue || lastRecoveraidValue) {
      let currentDomain = window.location.hostname;
      if(currentDomain == 'www.crazysales.com.au') {
        currentDomain = ".crazysales.com.au"
      }
      setCookie('affiliateid', lastAidValue || lastRecoveraidValue, { expires: 30, secure: true, domain: `${currentDomain}`, })
    }
  }, [router.query])

  // 分页器Change事件
  const onChange: PaginationProps['onChange'] = (current) => {
    setPage(current)
    setUpdating(true)
    window.scrollTo({ top: 0 })
  };

  // 切换pageSize
  const handleChangePageSize = (pageSize: number) => {
    setPageSize(pageSize)
  };

  // 设置页面排序的选项和默认值
  useEffect(() => {
    setSuburbOptions(
      [
        { id: '1', name: 'Best Match' },
        { id: '2', name: 'Best Selling' },
        { id: '3', name: 'Price(High to Low)' },
        { id: '4', name: 'Price(Low to High)' },
        { id: '5', name: 'New Arrivals' }
      ]
    )

    setSortBy('1')
  }, [])

  // 切换free shipping按钮
  const handleToggleFreeShipping = () => {
    if (isFreeShipping) {
      setIsFreeShipping(false)
    } else {
      setIsFreeShipping(true)
    }
  }

  // Sort by排序选择器触发事件
  const handleChangeSort = (value: string) => {
    // 匹配 Filter by 参数
    switch (String(value)) {
      case '1':
        setSortByFilter({ sortBy: 'bestMatch', sortDirection: 'desc' })
        break
      case '2':
        setSortByFilter({ sortBy: 'popularity', sortDirection: 'desc' })
        break
      case '3':
        setSortByFilter({ sortBy: 'price', sortDirection: 'desc' })
        break
      case '4':
        setSortByFilter({ sortBy: 'price', sortDirection: 'asc' })
        break
      case '5':
        setSortByFilter({ sortBy: 'addTime', sortDirection: 'desc' })
        break
      default:
    }
    setSortBy(value)
  };

  // 切换上一页/下一页
  const handleTurnPage = (turnPage: number) => {
    // turnPage 小于1  或者  大于最后一页的页码，都return掉
    if (turnPage < 1 || turnPage > lastPage) return

    setPage(turnPage)
  }

  // sidebar category change事件
  const handleFilterSelectCategory = (categoryItem: cateFilterItem) => {
    /**
     * 如果当前分类筛选与点选的分类相同，则取消分类筛选
     * 分类不相同，则增加分类筛选
     * 跳转使用router.push的方式，并且加上shallow配置，url变化，但页面是静态刷新
     */
    let hrefUrl = ''
    if (categoryItem.name == pageParams.currentCategoryName) {
      hrefUrl = `/shop/${String(searchKeyword).replace(/ /g, '+')}`
    } else {
      hrefUrl = `/shop/${String(searchKeyword).replace(/ /g, '+')}/category/${categoryItem.name.replace(/ /g, '+')}`
    }

    window.location.href = hrefUrl
  }

  // 路由变化的监听
  useEffect(() => {
    if (!isFirstLoad && !updating) {
      let keyword = ''
      let categoryFromUrl = ''

      let path = router.asPath
      path = path.replace('/shop/', '')
      let queryList = path.split('/')

      keyword = queryList[0].replace(/\+/g, " ")
      if (queryList.length > 1 && queryList[1] == 'category') {
        categoryFromUrl = queryList[2]
      }
      setIsFirstLoad(true)
      setUpdating(true)

      // 格式化一些筛选项
      setPageSize(48)
      setPerPage(48)
      setSortBy('1')
      setSortByFilter({ sortBy: '', sortDirection: '' })

      getPageData(keyword, categoryFromUrl)
    }
    
  }, [router.asPath])

  // sidebar filter 点击事件
  const handleFilterItemSelect = (filterKey: string, itemName: string) => {
    /**
     * 接收到参数
     */

    let filterData = JSON.parse(JSON.stringify(sidebarFilter))

    switch (filterKey) {
      case 'hot-brand':
        filterData.forEach((item: filterTypeItem) => {
          if (item.key == 'hot-brand') {
            if (item.currentItemName != itemName) {
              item.currentItemName = itemName
            } else {
              item.currentItemName = ''
            }
          }
        });
        break
      case 'hot-search':
        filterData.forEach((item: filterTypeItem) => {
          if (item.key == 'hot-search') {
            if (item.currentItemName != itemName) {
              item.currentItemName = itemName
            } else {
              item.currentItemName = ''
            }
          }
        });
        break
      case 'price-range':
        filterData.forEach((item: filterTypeItem) => {
          if (item.key == 'price-range') {
            if (item.currentItemName != itemName) {
              item.currentItemName = itemName
            } else {
              item.currentItemName = ''
            }
          }
        });
        break
      default:
        // 默认，把参数剔除
        filterData.forEach((item: filterTypeItem) => {
          item.currentItemName = ''
        });
        break
    }

    setPage(1)
    setSidebarFilter(filterData)
  }

  // 更新页面数据
  const handleUpdateData = async (params: any, sidebarFilterQuery: any[]) => {
    try {
      if (updating) return
      setUpdating(true)

      const data = await getSearchData(params)

      // 结束loading，给100ms的延迟，防止页面抖动
      setTimeout(() => {
        setUpdating(false)
      }, 100)

      if (data) {
        if (data.link && data.link.replaceUrl) {
          window.location.href = data.link.replaceUrl
        }

        setLastPage(data.pageList.lastPage)
        setPerPage(data.pageList.perPage)
        setTotal(data.pageList.total)
        setPageProductData(data.pageList.items)
  
        setBreadCrumb(data.breadCrumb)
        setRelatedKeywords(data.relatedKeywords)
        setHotSearchKeyword(data.hotSearchKeyword)


        let filtersData = data.sidebarFilter.filter((item: any) => item.key != 'category')
        let cateData = data.sidebarFilter.filter((item: any) => item.key == 'category')

        filtersData.forEach((item: any) => {
          let target = sidebarFilterQuery.filter((targetItem: any) => targetItem.key == item.key)

          if (target.length > 0) {
            item.currentItemName = target[0].currentItemName || ''
          }
          if ((!item.items || item.items.length <= 0) &&  target[0].currentItem.name) {
            item.items.push(target[0].currentItem)
          }
        });

        let filtersDataResult = filtersData.filter((item: any) => item.items.length > 0)
        setSidebarFilter(filtersDataResult || [])

        // 新的sidebar数据
        if (!cateData[0].items || cateData[0].items.length <= 0) {
          if (!params.category) {
            cateData = []
            setSidebarCateFilter(cateData)
          }
        } else if (!params.category) {
          setSidebarCateFilter(cateData)
        }
      }
    } catch (error) {
      setUpdating(false)
    }
  }

  // 处理请求参数
  const handleQueryParams = (
    page: number, 
    pageSize: number,
    sortByFilter: sortByFilterType,
    sidebarFilterQuery: any[],
    isFreeShipping: boolean
  ) => {
    let dataObj = { 
      keyword: searchKeyword,
      page,
      perPage: pageSize,
      category: searchCategoryFromUrl,
      brand: '',
      hot: '',
      minPrice: '',
      maxPrice: '',
      freeShipping: isFreeShipping ? 1 : 0,
      ...sortByFilter
    }

    if (!dataObj.sortBy || !dataObj.sortDirection) {
      dataObj.sortBy = 'bestMatch'
      dataObj.sortDirection = 'desc'
    }

    let filterData = JSON.parse(JSON.stringify(sidebarFilterQuery))

    filterData.forEach((item: any) => {
      if (item.currentItemName) {
        switch (item.key) {
          case 'hot-brand':
            dataObj.brand = item.currentItemName
            break
          case 'hot-search':
            dataObj.hot = item.currentItemName
            break
          case 'price-range': {
            if (item.currentItem) {
              if (item.currentItem.minPrice) dataObj.minPrice = String(item.currentItem.minPrice)
              if (item.currentItem.maxPrice) dataObj.maxPrice = String(item.currentItem.maxPrice)
            }
            break
          }
          default:
            break
        }
      }
    });

    // 剔除无用的参数
    let paramsObj = Object.fromEntries(
      Object.entries(dataObj).filter(([_, value]) => value !== null && value !== '')
    )

    return paramsObj
  }

  // 定义一个去抖函数，延迟300ms执行
  const debouncedFetchProducts = useCallback(
    debounce(async (page, pageSize, sortByFilter, sidebarFilterQuery, isFreeShipping) => {
      try {
        let params = handleQueryParams(page, pageSize, sortByFilter, sidebarFilterQuery, isFreeShipping)
        handleUpdateData(params, sidebarFilterQuery)
      } catch (error) {
        console.error('Failed to fetch products', error);
      }
    }, 200),
    [searchKeyword, searchCategoryFromUrl] // 如果筛选条件和分页状态是不会变的，可以把它们包含到依赖数组中。否则保持数组为空。
  );

  // 当筛选条件或分页状态变化时执行去抖后的数据获取函数
  useEffect(() => {
    if (isFirstLoad) {
      setTimeout(() => {
        setIsFirstLoad(false)
      }, 300);
    } else {
      debouncedFetchProducts(page, pageSize, sortByFilter, sidebarFilterQuery, isFreeShipping);
    }
  }, [page, pageSize, sortByFilter, sidebarFilterQuery, isFreeShipping]);

  // filter 取消事件
  const handleCancelFilter = (filterType: string) => {
    // 直接使用数组映射保持不可变性
    const newFilterData = sidebarFilter.map(item => {
      // 合并判断逻辑：匹配指定类型或处理默认情况
      const isTarget = !!(item.title === filterType)
  
      return isTarget 
        ? { ...item, currentItemName: '' }
        : item;
    });
  
    setPage(1);
    setSidebarFilter(newFilterData);
  };

  // 分类 取消事件
  const handleCancelCategoryFilter = () => {
    window.location.href = `/shop/${String(searchKeyword).replace(/ /g, '+')}`
  };

  // 当筛选条件变化时输出正确的筛选text
  useEffect(() => {
    let str = ''
    const divs: React.ReactElement[] = [];

    if (searchCategoryFromUrl) {
      str = ` In Category:${searchCategoryFromUrl}`
      divs.push(
        <div key={'category'} style={{ display: 'flex', alignItems: 'center' }}>
          &nbsp;In&nbsp;
          <div className={styles['filter-divs']}>
            Category: {searchCategoryFromUrl}
            <div 
              className={styles['cancel-btn']} 
              onClick={() => handleCancelCategoryFilter()}
            >
              <CloseOutlined style={{ color: '#FFFFFF' }} />
            </div>
          </div>
        </div>
      );
    }
    
    let target = sidebarFilter.filter((item: filterTypeItem) => item.currentItemName)
    if (target.length) {
      target.forEach((item: filterTypeItem, index: number) => {
        if (str || index >= 1) {
          str = `${str} > ${item.title}:${item.currentItemName}`
        } else {
          str = ` In ${item.title}:${item.currentItemName}`
        }

        divs.push(
          <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
            {index > 0 || searchCategoryFromUrl ? ' > ' : ' In '}
            <div className={styles['filter-divs']}>
              {item.title}: {item.currentItemName}
              <div 
                className={styles['cancel-btn']} 
                onClick={() => handleCancelFilter(item.title)}
              >
                <CloseOutlined style={{ color: '#FFFFFF' }} />
              </div>
            </div>
          </div>
        );
      })
    }

    if (str != filterStr) {
      setFilterStr(str)
    }
    setFilterDivs(divs);
  }, [sidebarFilter]);

  // 当筛选条件变化时输出正确的筛选text
  useEffect(() => {
    let sidebarFilterCopy: any[] = []

    sidebarFilter.forEach((item: filterTypeItem) => {
      let sidebarFilterItem = {
        key: item.key,
        title: item.title,
        currentItemName: item.currentItemName || '',
        currentItem: {}
      }
      let target = item.items.filter((v: sideFilterItem) => v.name == item.currentItemName)
      if (target.length) {
        sidebarFilterItem.currentItem = target[0]
      }
      sidebarFilterCopy.push(sidebarFilterItem)
    })

    if (JSON.stringify(sidebarFilterCopy) != JSON.stringify(sidebarFilterQuery)) {
      setSidebarFilterQuery(sidebarFilterCopy)
    }
  }, [filterStr]);

  // 页面插入iframe，植入poter
  useEffect(() => {
    setTimeout(() => {
      // 创建一个 iframe 元素
      var iframe = document.createElement('iframe');
      iframe.src = 'https://www.poter.com.au/';

      // 设置 iframe 的样式使其不可见，再将 iframe 插入到 body 中
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }, 1000);
  }, []);

  return (
    <>
      <Head>
        <title>Online Shopping Australia - Beauty & Health, Computers, Toys, - Deals & Discounts | CrazySales</title>
        <meta name="keywords" content={`Online Shopping Australia, online shopping sites, Home Shopping, Shopping Deals, Best Deals, Home Garden, Pet Supplies, Sports Fitness, Bedding Bath, Buy Online, Discount Shopping, Toys Hobbies, Beauty Health, Crazy Sales, Computers, IT, Christmas, Electronics, Automotive, Tools, Appliances, Baby, CrazySales`}></meta>
        <meta name="description" content={`CrazySales.com.au - Australia's #1 online shopping website! Cheap bargains & discounts with the top online shopping Australia store! From furniture, to toys & hobbies, to appliances & pet supplies -- we've got it all, with free shipping on selected items!`}></meta>
      </Head>

      <DefaultLayout notStickyNavbar={true}>
        <div className={`${styles['page-search']} slider-wapper`}>
          <SliderBarLeft></SliderBarLeft>
          <SliderBarRight></SliderBarRight>

          {/* 面包屑 */}
          {
            !!(breadCrumb && breadCrumb.length) &&
            <BreadCrum BreadList={breadCrumb}></BreadCrum>
          }

          {/* 商城特色介绍 */}
          <div className={styles['shop-info-box']}>
            <ShopInfo hiddenContactus={true}></ShopInfo>
          </div>
          
          {
            !!(relatedKeywords && relatedKeywords.length) &&
            <div className={`${styles['about-box']} margin-top-16`}>
              <div className={styles['popular-category']}>
                Related Keywords:&nbsp;
                {
                  relatedKeywords.map((item: relatedKeywordItem, index: number) => (
                    <span key={index}>
                      <Link
                        href={item.url}
                        legacyBehavior
                      >
                        <a rel="noopener noreferrer">
                          {item.name}
                        </a>
                      </Link>
                      {
                        !!(index + 1 < relatedKeywords.length) &&
                        <span>&nbsp;|&nbsp;</span>
                      }
                    </span>
                  ))
                }
              </div>
            </div>
          }

          <div className={styles['page-container']}>
            <div className={styles['filter-box']}>

              <Spin spinning={updating}>
                {/* 其他后端返回的筛选 */}
                {
                  !!(sidebarFilter && sidebarFilter.length) &&
                  <div className={styles['filter-list']}>
                    {
                      sidebarFilter.map((item: filterTypeItem) => (
                        <div className={styles['filter-item']} key={item.key}>
                          <FilterItem 
                            filterTypeItem={item}
                            onItemSelect={handleFilterItemSelect}
                          ></FilterItem>
                        </div>
                      ))
                    }
                  </div>
                }

                {/* 分类的筛选 */}
                <div className={styles['category-filter']}>
                  <div className={styles['category-filter-content']}>
                    <CategoryFilter 
                      cateFilterData={sidebarCateFilter}
                      onSelectCategory={handleFilterSelectCategory}
                    ></CategoryFilter>
                  </div>
                </div>
              </Spin>

              {/* 新品上市或者热门商品推荐 */}
              {
                !!(categoryId) && 
                <div className={styles['new-arrivals']}>
                  <NewArrivals 
                    type={'hotProducts'}
                    category={categoryId}
                    title={`New Arrivals & Hot`}
                  ></NewArrivals>
                </div>
              }

              {/* 广告 */}
              <div style={{ marginTop: '20px' }}>
                <GoogleADSense />
              </div>
            </div>

            <div className={styles['container-box']}>
              <Spin spinning={updating}>
                <div className={styles['top-filter-box']}>
                  <div className={styles['top-jump']}>
                    <div className={styles['result-and-filters']}>
                      <div className={`${styles['high-light']} margin-right-5`}>{total}</div>
                      Results for
                      <div className={`${styles['high-light']} margin-left-5 margin-right-5`}>{searchKeyword}</div>
                      {filterDivs.length > 0 && (
                        <div className={styles['filters-box']}>
                          {filterDivs}
                        </div>
                      )}
                    </div>

                    <div className={styles['top-jump-box']}>
                      <div><span className={`${styles['high-light']}`}>{page}</span> / {lastPage}</div>
                      {/* 上一页 */}
                      <div 
                        className={`
                          ${styles['top-jump-item']} 
                          ${page <= 1 ? styles['disabled'] : ''}
                        `}
                        onClick={() => { handleTurnPage(page - 1) }}
                      >
                        <CaretLeftOutlined style={ page <= 1 ? { color: '#dddddd' } : undefined }/>
                      </div>

                      {/* 下一页 */}
                      <div 
                        className={`
                          ${styles['top-jump-item']} 
                          ${page >= lastPage ? styles['disabled'] : ''}
                        `}
                        onClick={() => { handleTurnPage(page + 1) }}
                      >
                        <CaretRightOutlined />
                      </div>
                    </div>
                  </div>

                  <div className={styles['page-size-filter']}>
                    <div className={styles['page-size']}>
                      Show:&nbsp;&nbsp;
                      {
                        pageSizeList.map((item: any) => (
                          <div 
                            className={`
                              ${styles['page-size-item']}
                              ${pageSize == item.value ? styles['active'] : ''}
                            `}
                            onClick={() => handleChangePageSize(item.value)}
                            key={item.id}
                          >{item.name}</div>
                        ))
                      }
                    </div>

                    <div className={styles['select-filter']}>
                      <div className={styles['select-filter-item']}>
                        Filtered by:&nbsp;

                        {
                          isFreeShipping
                          ?
                          <div className={styles['cancel-free-shipping']} onClick={handleToggleFreeShipping}>
                            Free Shipping
                            <div className={styles['cancel-btn']}>
                              <CloseOutlined style={{color: '#FFFFFF'}} />
                            </div>
                          </div>
                          :
                          <div className={styles['select-free-shipping']} onClick={handleToggleFreeShipping}>
                            Free Shipping
                          </div>
                        }
                      </div>

                      <div className={styles['select-filter-item']}>
                        Sort by:&nbsp;

                        {
                          !!(sortBy) &&
                          <Select 
                            onChange={handleChangeSort} 
                            size={'small'}
                            style={{ width: '180px' }}
                            className={styles['sort-by-select']}
                            defaultValue={sortBy}
                            value={sortBy}
                          >
                            {
                              !!(suburbOptions && suburbOptions.length) &&

                              suburbOptions.map((item: suburbOptionsItem) => (
                                <Option 
                                  value={item.id}
                                  key={item.id}
                                >
                                  {item.name}
                                </Option>
                              ))
                            }
                          </Select>
                        }
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles['product-list']}>
                  {
                    pageProductData && pageProductData.length
                    ?
                    pageProductData.map((product: ProductItem) => (
                      <div className={styles['product-item']} key={product.productID}>
                        <GoodsComponentWithDiscountNew {...{ ...product, productName: product.name, imgUrl: product.imgUrl }} />
                      </div>
                    ))
                    :
                    <>
                      {
                        !isFirstLoad ? (
                          <div className={styles['product-empty']}>Sorry, no products available with the selected options.</div>
                        ) : null
                      }
                    </>
                  }
                </div>

                {/* 分页器 */}
                {
                  !!(total && lastPage > 1) &&
                  <div className={styles['pagination-box']}>
                    <Pagination
                      key={perPage * total}
                      showQuickJumper
                      showSizeChanger={false}
                      current={page}
                      onChange={onChange}
                      defaultPageSize={perPage}
                      total={total}
                    />
                  </div>
                }
              </Spin>
              
              {
                !!(hotSearchKeyword && hotSearchKeyword.length) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']}>
                    Hot Searches:&nbsp;
                    {
                      hotSearchKeyword.map((item: hotSearchKeywordItem, index: number) => (
                        <span key={item.url}>
                          <Link
                            href={item.url}
                            legacyBehavior
                          >
                            <a rel="noopener noreferrer">
                              {item.keywords}
                            </a>
                          </Link>
                          {
                            !!(index + 1 < hotSearchKeyword.length) &&
                            <span>&nbsp;,&nbsp;</span>
                          }
                        </span>
                      ))
                    }
                  </div>
                </div>
              }
              
              {
                !!(popularKeywords) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']} dangerouslySetInnerHTML={{ __html: `Popular Category: ${popularKeywords}` }}></div>
                </div>
              }
              
              {
                !!(shopArea && shopArea.length) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['popular-category']}>
                    Shop { searchKeyword.toString().toLowerCase() } By City:&nbsp;
                    {
                      shopArea.map((item: areaItem, index: number) => (
                        <span key={index}>
                          <Link
                            href={item.url}
                            legacyBehavior
                          >
                            <a rel="noopener noreferrer">
                            { searchKeyword.toString().toLowerCase() } {item.name}
                            </a>
                          </Link>
                          {
                            !!(index + 1 < shopArea.length) &&
                            <span>&nbsp;,&nbsp;</span>
                          }
                        </span>
                      ))
                    }
                  </div>
                </div>
              }
              
              {
                !!(categoryId) && 
                <LatestReviews categoryId={categoryId}></LatestReviews>
              }

              {
                !!(aboutCategory) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['header']}>
                    About&nbsp;{categoryName}
                  </div>

                  <div className={styles['content']} dangerouslySetInnerHTML={{ __html: aboutCategory }}></div>
                </div>
              }
              
              <div style={{ marginTop: '20px' }}>
                <GoogleADSense />
              </div>
            </div>
          </div>

        </div>
      </DefaultLayout>
    </>
  )
}
